// Simplified and corrected KitchenDisplay.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from 'react-router-dom';

const url = process.env.REACT_APP_SERVER_URL || "https://food.stall-centralsoft.com";

export default function KitchenDisplay() {
  const stallID = localStorage.getItem("stallID");
  const stallName = localStorage.getItem("username");
  const [orders, setOrders] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Fetching orders for stall:", stallID);

      // Fetch pending orders
      const ordersResponse = await axios.get(`${url}/api/admin/orders/pending`, {
        params: {
          stallID,
          status: ['pending', 'preparing', 'ready']
        }
      });

      console.log("Orders response:", ordersResponse.data);

      let ordersData = ordersResponse.data.orders || [];
      
      if (ordersData.length === 0) {
        console.log("No orders found");
        setOrders([]);
        setLoading(false);
        return;
      }

      // Process each order to get items
      const ordersWithItems = await Promise.all(
        ordersData.map(async (order) => {
          try {
            // Get order items
            const itemsResponse = await axios.get(`${url}/api/admin/order-items`, {
              params: {
                orderID: order.orderID,
                stallID: stallID
              }
            });

            const items = itemsResponse.data.items || [];
            
            return {
              ...order,
              items: items.map(item => ({
                ...item,
                status: item.status || 'pending'
              })),
              time: new Date(order.orderDate).toLocaleTimeString('en-MY', {
                hour: '2-digit',
                minute: '2-digit'
              }),
              date: new Date(order.orderDate).toLocaleDateString('en-MY'),
              // Calculate order total from items
              total: items.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0)
            };
          } catch (error) {
            console.error(`Error fetching items for order ${order.orderID}:`, error);
            return {
              ...order,
              items: [],
              time: new Date(order.orderDate).toLocaleTimeString('en-MY'),
              date: new Date(order.orderDate).toLocaleDateString('en-MY'),
              total: 0
            };
          }
        })
      );

      setOrders(ordersWithItems);
      console.log("Processed orders:", ordersWithItems);

    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [stallID]);

  // Update item status
  const updateItemStatus = async (orderID, orderItemID, newStatus) => {
    try {
      console.log(`Updating item ${orderItemID} to ${newStatus}`);
      
      await axios.put(`${url}/api/admin/order-items/${orderItemID}/status`, {
        status: newStatus
      });

      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order => {
          if (order.orderID === orderID) {
            return {
              ...order,
              items: order.items.map(item =>
                item.orderItemID === orderItemID
                  ? { ...item, status: newStatus }
                  : item
              )
            };
          }
          return order;
        })
      );

      // Refresh orders after a short delay
      setTimeout(() => {
        fetchOrders();
      }, 1000);

    } catch (error) {
      console.error("Error updating item status:", error);
      alert(`Failed to update status: ${error.response?.data?.error || error.message}`);
    }
  };

  // Update order status
  const updateOrderStatus = async (orderID, newStatus) => {
    try {
      await axios.put(`${url}/api/admin/orders/${orderID}/status`, {
        status: newStatus
      });

      // Remove from local state if cancelled/completed
      if (newStatus === 'cancelled' || newStatus === 'completed') {
        setOrders(prevOrders => prevOrders.filter(order => order.orderID !== orderID));
      } else {
        // Update status in local state
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.orderID === orderID
              ? { ...order, status: newStatus }
              : order
          )
        );
      }

    } catch (error) {
      console.error("Error updating order status:", error);
      alert(`Failed to update order: ${error.response?.data?.error || error.message}`);
    }
  };

  // Auto-refresh
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Filter orders
  const filteredOrders = activeFilter === 'all'
    ? orders
    : orders.filter(order => order.status === activeFilter);

  // Helper function to get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'preparing': return 'info';
      case 'ready': return 'success';
      case 'delivered': return 'primary';
      case 'completed': return 'secondary';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading kitchen orders...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="bg-dark text-white p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="mb-0">👨‍🍳 Kitchen Display</h1>
            <p className="mb-0">Stall: <strong>{stallName}</strong> | Active Orders: {orders.length}</p>
          </div>
          <div>
            <button className="btn btn-primary me-2" onClick={fetchOrders}>
              🔄 Refresh
            </button>
            <button className="btn btn-light" onClick={() => navigate('/ordering')}>
              ← Ordering
            </button>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="btn-group" role="group">
            <button className={`btn ${activeFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveFilter('all')}>
              All ({orders.length})
            </button>
            <button className={`btn ${activeFilter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={() => setActiveFilter('pending')}>
              Pending ({orders.filter(o => o.status === 'pending').length})
            </button>
            <button className={`btn ${activeFilter === 'preparing' ? 'btn-info' : 'btn-outline-info'}`}
              onClick={() => setActiveFilter('preparing')}>
              Preparing ({orders.filter(o => o.status === 'preparing').length})
            </button>
            <button className={`btn ${activeFilter === 'ready' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setActiveFilter('ready')}>
              Ready ({orders.filter(o => o.status === 'ready').length})
            </button>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="row">
        {filteredOrders.length === 0 ? (
          <div className="col-12">
            <div className="alert alert-info text-center">
              <h4>No {activeFilter !== 'all' ? activeFilter : ''} orders</h4>
              <p>When new orders come in, they will appear here.</p>
            </div>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div key={order.orderID} className="col-lg-6 col-xl-4 mb-4">
              <div className={`card h-100 border-${getStatusColor(order.status)}`}>
                
                {/* Order Header */}
                <div className={`card-header text-white bg-${getStatusColor(order.status)}`}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-1">Table {order.tableNumber}</h5>
                      <small>{order.time}</small>
                    </div>
                    <div className="text-end">
                      <div className="badge bg-light text-dark">#{order.orderID.substring(0, 8)}</div>
                      <div className="fw-bold mt-1">RM {order.total.toFixed(2)}</div>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="card-body">
                  {order.items.length === 0 ? (
                    <p className="text-muted">No items in this order</p>
                  ) : (
                    order.items.map(item => (
                      <div key={item.orderItemID} className="mb-3 pb-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="fw-bold mb-1">
                              {item.quantity}x {item.foodName}
                            </h6>
                            {item.notes && (
                              <small className="text-muted d-block">📝 {item.notes}</small>
                            )}
                          </div>
                          <span className={`badge bg-${getStatusColor(item.status)}`}>
                            {item.status}
                          </span>
                        </div>

                        {/* Item Action Buttons */}
                        <div className="btn-group w-100" role="group">
                          {item.status === 'pending' && (
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => updateItemStatus(order.orderID, item.orderItemID, 'preparing')}
                            >
                              🍳 Start Preparing
                            </button>
                          )}

                          {item.status === 'preparing' && (
                            <button
                              className="btn btn-info btn-sm"
                              onClick={() => updateItemStatus(order.orderID, item.orderItemID, 'ready')}
                            >
                              ✅ Mark Ready
                            </button>
                          )}

                          {item.status === 'ready' && (
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => updateItemStatus(order.orderID, item.orderItemID, 'delivered')}
                            >
                              🚚 Mark Delivered
                            </button>
                          )}

                          {item.status === 'delivered' && (
                            <span className="btn btn-secondary btn-sm disabled">
                              ✓ Delivered
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Order Action Buttons */}
                <div className="card-footer">
                  <div className="d-flex gap-2">
                    {/* Cancel Order Button - Always visible */}
                    <button
                      className="btn btn-danger btn-sm flex-grow-1"
                      onClick={() => updateOrderStatus(order.orderID, 'cancelled')}
                    >
                      ❌ Cancel Order
                    </button>

                    {/* Complete Order Button - Only when all items delivered */}
                    {order.items.every(item => item.status === 'delivered') && (
                      <button
                        className="btn btn-success btn-sm flex-grow-1"
                        onClick={() => updateOrderStatus(order.orderID, 'completed')}
                      >
                        ✅ Complete Order
                      </button>
                    )}

                    {/* Mark as Ready Button - For entire order */}
                    {order.status === 'preparing' && order.items.some(item => item.status !== 'delivered') && (
                      <button
                        className="btn btn-success btn-sm flex-grow-1"
                        onClick={() => {
                          // Mark all items as ready
                          order.items.forEach(item => {
                            if (item.status === 'preparing') {
                              updateItemStatus(order.orderID, item.orderItemID, 'ready');
                            }
                          });
                        }}
                      >
                        📦 Mark All Ready
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="text-center mt-4">
        <small className="text-muted">
          🔄 Auto-refreshes every 30 seconds • Last update: {new Date().toLocaleTimeString()}
        </small>
      </div>
    </div>
  );
}